"use client";

import { useFilterStore } from "@faire/deprecated-retailer-visitor-shared/components/Filters/useFilterStore";
import {
  assignInlineVars,
  DynamicVariable,
} from "@faire/deprecated-retailer-visitor-shared/lib/tailwindUtils";
import { Column } from "@faire/web/slate/Layout";
import { ResponsiveValues } from "@faire/web/slate/Theme/ResponsiveValues";
import { cn } from "@faire/web/tools/cn";
import React from "react";

const NUM_TILES = 16;
const NUM_TILES_PER_ROW_MOBILE = 2;
const NUM_TILES_PER_ROW_TABLET = 4;
const NUM_TILES_PER_ROW_DESKTOP = 4;
const NUM_TILES_PER_ROW_XLARGE = 4;
const NUM_TILES_PER_ROW: ResponsiveValues<number> = {
  mobileAndAbove: NUM_TILES_PER_ROW_MOBILE,
  tabletAndAbove: NUM_TILES_PER_ROW_TABLET,
  desktopAndAbove: NUM_TILES_PER_ROW_DESKTOP,
  xLargeAndAbove: NUM_TILES_PER_ROW_XLARGE,
};

const varMobileNumColumns: DynamicVariable<"--f-sdui-fl-nc-m"> = {
  variable: "--f-sdui-fl-nc-m",
  className: "grid-cols-(--f-sdui-fl-nc-m)",
};

const varTabletNumColumns: DynamicVariable<"--f-sdui-fl-nc-md"> = {
  variable: "--f-sdui-fl-nc-md",
  className: "md:grid-cols-(--f-sdui-fl-nc-md)",
};

const varDesktopNumColumns: DynamicVariable<"--f-sdui-fl-nc-lg"> = {
  variable: "--f-sdui-fl-nc-lg",
  className: "lg:grid-cols-(--f-sdui-fl-nc-lg)",
};

const varXLargeNumColumns: DynamicVariable<"--f-sdui-fl-nc-xl"> = {
  variable: "--f-sdui-fl-nc-xl",
  className: "xl:grid-cols-(--f-sdui-fl-nc-xl)",
};

export const SDUIFilterablePaginatedSectionGridLoadingSkeleton = ({
  loadingSkeletonNode,
  numTiles,
  numTilesPerRowResponsive,
  allowCollapsedLayout,
}: {
  loadingSkeletonNode: React.ReactNode;
  numTiles?: number;
  numTilesPerRowResponsive?: ResponsiveValues<number>;
  allowCollapsedLayout?: boolean;
}) => {
  const {
    data: { isSideBarVisible },
  } = useFilterStore(["isSideBarVisible"]);
  const isCollapsibleFilterSidebarOpen =
    !!allowCollapsedLayout && isSideBarVisible;

  const numTilesPerRowDesktopDefault =
    numTilesPerRowResponsive?.desktopAndAbove ?? NUM_TILES_PER_ROW_DESKTOP;
  const numTilesPerRowXLargeDefault =
    numTilesPerRowResponsive?.xLargeAndAbove ?? NUM_TILES_PER_ROW_XLARGE;
  const numTilesPerRowDesktop = isCollapsibleFilterSidebarOpen
    ? numTilesPerRowDesktopDefault - 1
    : numTilesPerRowDesktopDefault;
  const numTilesPerRowXLarge = isCollapsibleFilterSidebarOpen
    ? numTilesPerRowXLargeDefault - 1
    : numTilesPerRowXLargeDefault;
  return (
    <Column className="h-[3500px] w-full">
      {/* Tiles */}
      <div
        className={cn(
          "grid gap-4",
          varMobileNumColumns.className,
          varTabletNumColumns.className,
          varDesktopNumColumns.className,
          varXLargeNumColumns.className
        )}
        style={assignInlineVars({
          [varMobileNumColumns.variable]: `repeat(${numTilesPerRowResponsive?.mobileAndAbove ?? NUM_TILES_PER_ROW.mobileAndAbove}, minmax(0, 1fr))`,
          [varTabletNumColumns.variable]: `repeat(${numTilesPerRowResponsive?.tabletAndAbove ?? NUM_TILES_PER_ROW.tabletAndAbove}, minmax(0, 1fr))`,
          [varDesktopNumColumns.variable]: `repeat(${numTilesPerRowDesktop}, minmax(0, 1fr))`,
          [varXLargeNumColumns.variable]: `repeat(${numTilesPerRowXLarge}, minmax(0, 1fr))`,
        })}
      >
        {Array.from({ length: numTiles ?? NUM_TILES }).map((_, index) => (
          <React.Fragment key={index}>{loadingSkeletonNode}</React.Fragment>
        ))}
      </div>
    </Column>
  );
};
